import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertController } from '@ionic/angular';
import { LanguageService } from './language.service';
import { LocalAuthService } from './auth.service';

/** Error code of the API when the token's session was ended (security page of the customer account). */
export const API_ERROR_SESSION_REVOKED = 'session_revoked';

/** True for a 401 response whose body is `{ error: 'session_revoked' }`. */
export function isSessionRevokedError(error: unknown): boolean {
  return (
    error instanceof HttpErrorResponse &&
    error.status === 401 &&
    (error.error as { error?: string } | null)?.error === API_ERROR_SESSION_REVOKED
  );
}

/**
 * The API answers 401 `session_revoked` once the session was ended elsewhere. The token itself is still
 * valid, so a re-login redirect would silently sign the user straight back in: tell them, then log out
 * without any automatic login redirect. Shown once, however many requests fail at the same time.
 */
@Injectable({ providedIn: 'root' })
export class SessionRevokedService {
  private readonly alertController = inject(AlertController);
  private readonly languageService = inject(LanguageService);
  private readonly auth = inject(LocalAuthService);

  private shown = false;

  async handle(): Promise<void> {
    if (this.shown) {
      return;
    }
    this.shown = true;

    const alert = await this.alertController.create({
      header: this.languageService.translate('auth.sessionRevoked.title'),
      message: this.languageService.translate('auth.sessionRevoked.message'),
      backdropDismiss: false,
      keyboardClose: false,
      buttons: [
        { text: this.languageService.translate('auth.sessionRevoked.confirm'), role: 'confirm' },
      ],
    });
    await alert.present();
    await alert.onDidDismiss();
    await this.auth.logout({ skipAudit: true });
  }
}
